import { api } from "@/redux/api";

import {
	BulkCreateFieldMappings,
	AiTestRequest,
	AiTestResponse,
	AiTestResponseSchema,
	CompanyLeadSyncTriggerResponse,
	CompanyLeadSyncTriggerResponseSchema,
	CompanyDealCreationTriggerResponse,
	CompanySyncTriggerResponse,
	CreateFieldMapping,
	CreateHubspotDealRequest,
	CreateHubspotDealResponse,
	CreateHubspotDealResponseSchema,
	DealConfig,
	DealConfigResponse,
	FieldMapping,
	FieldMappingsResponse,
	HubspotApiKeyResponse,
	HubspotEntityType,
	HubspotFieldsResponse,
	HubspotOwnersResponse,
	HubspotPipelinesResponse,
	HubspotFieldsResponseSchema,
	HubspotPipelinesResponseSchema,
	HubspotResyncResponse,
	HubspotSyncLogsResponse,
	HubspotValidateResponse,
	LeadConfig,
	LeadConfigResponse,
	LeadConfigResponseSchema,
	ListLeadSyncTriggerResponse,
	ListLeadSyncTriggerResponseSchema,
	ListSyncTriggerResponse,
	CreateUserMapping,
	OAuthAuthorizeResponse,
	OAuthDisconnectResponse,
	OAuthStatusResponse,
	SourceFieldsResponse,
	UpdateCompanyDealCreationTrigger,
	UpdateCompanyLeadSyncTrigger,
	ConversationSyncResponse,
	UpdateConversationSync,
	UpdateCompanySyncTrigger,
	UpdateFieldMapping,
	UpdateListLeadSyncTrigger,
	UpdateListSyncTrigger,
	UserMapping,
	UserMappingsResponse,
} from "../../pages/Company/schema/hubspot";

export const hubspotApi = api.injectEndpoints({
	endpoints: (builder) => ({
		// API Key Management
		saveHubspotApiKey: builder.mutation<HubspotApiKeyResponse, { apiKey: string }>({
			query: (body) => ({
				url: "hubspot/api-key",
				method: "POST",
				body,
			}),
			invalidatesTags: ["Hubspot", "Me", "Company"],
		}),
		deleteHubspotApiKey: builder.mutation<HubspotApiKeyResponse, void>({
			query: () => ({
				url: "hubspot/api-key",
				method: "DELETE",
			}),
			invalidatesTags: ["Hubspot", "Me", "Company"],
		}),
		validateHubspotApiKey: builder.query<HubspotValidateResponse, void>({
			query: () => ({
				url: "hubspot/api-key/validate",
				method: "GET",
			}),
			providesTags: ["Hubspot"],
		}),

		// Field Retrieval
		getHubspotContactFields: builder.query<HubspotFieldsResponse, void>({
			query: () => ({
				url: "hubspot/fields/contacts",
				method: "GET",
			}),
			providesTags: ["Hubspot"],
		}),
		getHubspotCompanyFields: builder.query<HubspotFieldsResponse, void>({
			query: () => ({
				url: "hubspot/fields/companies",
				method: "GET",
			}),
			providesTags: ["Hubspot"],
		}),
		getHubspotLeadFields: builder.query<HubspotFieldsResponse, void>({
			query: () => ({
				url: "hubspot/fields/leads",
				method: "GET",
				responseSchema: HubspotFieldsResponseSchema,
			}),
			providesTags: ["Hubspot"],
		}),
		getSourceFields: builder.query<SourceFieldsResponse, void>({
			query: () => ({
				url: "hubspot/source-fields",
				method: "GET",
			}),
			providesTags: ["Hubspot"],
		}),

		// Field Mapping CRUD
		getFieldMappings: builder.query<FieldMappingsResponse, { entityType?: HubspotEntityType }>({
			query: (params) => ({
				url: "hubspot/field-mappings",
				method: "GET",
				params,
			}),
			providesTags: ["Hubspot"],
		}),
		createFieldMapping: builder.mutation<FieldMapping, CreateFieldMapping>({
			query: (body) => ({
				url: "hubspot/field-mappings",
				method: "POST",
				body,
			}),
			invalidatesTags: ["Hubspot"],
		}),
		updateFieldMapping: builder.mutation<FieldMapping, { id: number; data: UpdateFieldMapping }>({
			query: ({ id, data }) => ({
				url: `hubspot/field-mappings/${id}`,
				method: "PUT",
				body: data,
			}),
			invalidatesTags: ["Hubspot"],
		}),
		deleteFieldMapping: builder.mutation<void, number>({
			query: (id) => ({
				url: `hubspot/field-mappings/${id}`,
				method: "DELETE",
			}),
			invalidatesTags: ["Hubspot"],
		}),
		bulkCreateFieldMappings: builder.mutation<FieldMappingsResponse, BulkCreateFieldMappings>({
			query: (body) => ({
				url: "hubspot/field-mappings/bulk",
				method: "POST",
				body,
			}),
			invalidatesTags: ["Hubspot"],
		}),
		testHubspotAiFieldMapping: builder.mutation<AiTestResponse, AiTestRequest>({
			query: (body) => ({
				url: "hubspot/field-mappings/ai/test",
				method: "POST",
				body,
				responseSchema: AiTestResponseSchema,
			}),
		}),

		// Sync Trigger Settings - Company
		getCompanySyncTrigger: builder.query<CompanySyncTriggerResponse, void>({
			query: () => ({
				url: "hubspot/sync-trigger/company",
				method: "GET",
			}),
			providesTags: ["Hubspot"],
		}),
		updateCompanySyncTrigger: builder.mutation<
			CompanySyncTriggerResponse,
			UpdateCompanySyncTrigger
		>({
			query: (body) => ({
				url: "hubspot/sync-trigger/company",
				method: "PUT",
				body,
			}),
			invalidatesTags: ["Hubspot", "Me", "Company"],
		}),

		// Sync Trigger Settings - List
		getListSyncTrigger: builder.query<ListSyncTriggerResponse, number>({
			query: (listId) => ({
				url: `hubspot/sync-trigger/list/${listId}`,
				method: "GET",
			}),
			providesTags: ["Hubspot"],
		}),
		updateListSyncTrigger: builder.mutation<
			ListSyncTriggerResponse,
			{ listId: number; data: UpdateListSyncTrigger }
		>({
			query: ({ listId, data }) => ({
				url: `hubspot/sync-trigger/list/${listId}`,
				method: "PUT",
				body: data,
			}),
			invalidatesTags: ["Hubspot"],
		}),

		// Deal Creation Trigger Settings - Company
		getCompanyDealCreationTrigger: builder.query<CompanyDealCreationTriggerResponse, void>({
			query: () => ({
				url: "hubspot/deal-creation-trigger/company",
				method: "GET",
			}),
			providesTags: ["Hubspot"],
		}),
		updateCompanyDealCreationTrigger: builder.mutation<
			CompanyDealCreationTriggerResponse,
			UpdateCompanyDealCreationTrigger
		>({
			query: (body) => ({
				url: "hubspot/deal-creation-trigger/company",
				method: "PUT",
				body,
			}),
			invalidatesTags: ["Hubspot", "Me", "Company"],
		}),

		// Lead Sync Trigger Settings - Company
		getCompanyLeadSyncTrigger: builder.query<CompanyLeadSyncTriggerResponse, void>({
			query: () => ({
				url: "hubspot/lead-sync-trigger/company",
				method: "GET",
				responseSchema: CompanyLeadSyncTriggerResponseSchema,
			}),
			providesTags: ["Hubspot"],
		}),
		updateCompanyLeadSyncTrigger: builder.mutation<
			CompanyLeadSyncTriggerResponse,
			UpdateCompanyLeadSyncTrigger
		>({
			query: (body) => ({
				url: "hubspot/lead-sync-trigger/company",
				method: "PUT",
				body,
				responseSchema: CompanyLeadSyncTriggerResponseSchema,
			}),
			invalidatesTags: ["Hubspot", "Me", "Company"],
		}),

		// Lead Sync Trigger Settings - List
		getListLeadSyncTrigger: builder.query<ListLeadSyncTriggerResponse, number>({
			query: (listId) => ({
				url: `hubspot/lead-sync-trigger/list/${listId}`,
				method: "GET",
				responseSchema: ListLeadSyncTriggerResponseSchema,
			}),
			providesTags: ["Hubspot"],
		}),
		updateListLeadSyncTrigger: builder.mutation<
			ListLeadSyncTriggerResponse,
			{ listId: number; data: UpdateListLeadSyncTrigger }
		>({
			query: ({ listId, data }) => ({
				url: `hubspot/lead-sync-trigger/list/${listId}`,
				method: "PUT",
				body: data,
				responseSchema: ListLeadSyncTriggerResponseSchema,
			}),
			invalidatesTags: ["Hubspot"],
		}),

		// Pipelines
		getHubspotPipelines: builder.query<HubspotPipelinesResponse, void>({
			query: () => ({
				url: "hubspot/pipelines",
				method: "GET",
			}),
			providesTags: ["Hubspot"],
		}),
		getHubspotLeadPipelines: builder.query<HubspotPipelinesResponse, void>({
			query: () => ({
				url: "hubspot/lead-pipelines",
				method: "GET",
				responseSchema: HubspotPipelinesResponseSchema,
			}),
			providesTags: ["Hubspot"],
		}),

		// Owners
		getHubspotOwners: builder.query<HubspotOwnersResponse, void>({
			query: () => ({
				url: "hubspot/owners",
				method: "GET",
			}),
			providesTags: ["Hubspot"],
		}),

		// Deal Fields
		getHubspotDealFields: builder.query<HubspotFieldsResponse, void>({
			query: () => ({
				url: "hubspot/fields/deals",
				method: "GET",
			}),
			providesTags: ["Hubspot"],
		}),

		// Deal Configuration
		getDealConfig: builder.query<DealConfigResponse, void>({
			query: () => ({
				url: "hubspot/deal-config",
				method: "GET",
			}),
			providesTags: ["Hubspot"],
		}),
		saveDealConfig: builder.mutation<DealConfigResponse, DealConfig>({
			query: (body) => ({
				url: "hubspot/deal-config",
				method: "PUT",
				body,
			}),
			invalidatesTags: ["Hubspot"],
		}),

		// Lead Configuration
		getLeadConfig: builder.query<LeadConfigResponse, void>({
			query: () => ({
				url: "hubspot/lead-config",
				method: "GET",
				responseSchema: LeadConfigResponseSchema,
			}),
			providesTags: ["Hubspot"],
		}),
		saveLeadConfig: builder.mutation<LeadConfigResponse, LeadConfig>({
			query: (body) => ({
				url: "hubspot/lead-config",
				method: "PUT",
				body,
				responseSchema: LeadConfigResponseSchema,
			}),
			invalidatesTags: ["Hubspot"],
		}),

		// User Mappings
		getUserMappings: builder.query<UserMappingsResponse, void>({
			query: () => ({
				url: "hubspot/user-mappings",
				method: "GET",
			}),
			providesTags: ["Hubspot"],
		}),
		createUserMapping: builder.mutation<UserMapping, CreateUserMapping>({
			query: (body) => ({
				url: "hubspot/user-mappings",
				method: "POST",
				body,
			}),
			invalidatesTags: ["Hubspot"],
		}),
		deleteUserMapping: builder.mutation<void, number>({
			query: (id) => ({
				url: `hubspot/user-mappings/${id}`,
				method: "DELETE",
			}),
			invalidatesTags: ["Hubspot"],
		}),

		// Create Deal
		createHubspotDeal: builder.mutation<CreateHubspotDealResponse, CreateHubspotDealRequest>({
			query: (body) => ({
				url: "hubspot/deals/create",
				method: "POST",
				body,
				responseSchema: CreateHubspotDealResponseSchema,
			}),
			invalidatesTags: ["Hubspot"],
		}),

		// Conversation Sync
		getConversationSync: builder.query<ConversationSyncResponse, void>({
			query: () => ({
				url: "hubspot/conversation-sync",
				method: "GET",
			}),
			providesTags: ["Hubspot"],
		}),
		updateConversationSync: builder.mutation<ConversationSyncResponse, UpdateConversationSync>({
			query: (body) => ({
				url: "hubspot/conversation-sync",
				method: "PUT",
				body,
			}),
			invalidatesTags: ["Hubspot"],
		}),

		// Sync Logs (PREC-4301)
		getHubspotSyncLogs: builder.query<
			HubspotSyncLogsResponse,
			{ entityType?: string; limit?: number; offset?: number } | void
		>({
			query: (params) => ({
				url: "hubspot/sync/logs",
				method: "GET",
				params: params || undefined,
			}),
			providesTags: ["Hubspot"],
		}),

		// Manual Resync
		triggerHubspotResync: builder.mutation<HubspotResyncResponse, void>({
			query: () => ({
				url: "hubspot/sync/resync",
				method: "POST",
			}),
			invalidatesTags: ["Hubspot"],
		}),

		// OAuth
		getOAuthAuthorizeUrl: builder.query<OAuthAuthorizeResponse, void>({
			query: () => ({
				url: "hubspot/oauth/authorize",
				method: "GET",
			}),
		}),
		getOAuthStatus: builder.query<OAuthStatusResponse, void>({
			query: () => ({
				url: "hubspot/oauth/status",
				method: "GET",
			}),
			providesTags: ["Hubspot"],
		}),
		disconnectOAuth: builder.mutation<OAuthDisconnectResponse, void>({
			query: () => ({
				url: "hubspot/oauth",
				method: "DELETE",
			}),
			invalidatesTags: ["Hubspot", "Me", "Company"],
		}),
	}),
});

export const {
	useSaveHubspotApiKeyMutation,
	useDeleteHubspotApiKeyMutation,
	useValidateHubspotApiKeyQuery,
	useLazyValidateHubspotApiKeyQuery,
	useGetHubspotContactFieldsQuery,
	useGetHubspotCompanyFieldsQuery,
	useGetHubspotLeadFieldsQuery,
	useGetSourceFieldsQuery,
	useGetFieldMappingsQuery,
	useCreateFieldMappingMutation,
	useUpdateFieldMappingMutation,
	useDeleteFieldMappingMutation,
	useBulkCreateFieldMappingsMutation,
	useTestHubspotAiFieldMappingMutation,
	useGetCompanySyncTriggerQuery,
	useUpdateCompanySyncTriggerMutation,
	useGetListSyncTriggerQuery,
	useUpdateListSyncTriggerMutation,
	useGetCompanyDealCreationTriggerQuery,
	useUpdateCompanyDealCreationTriggerMutation,
	useGetCompanyLeadSyncTriggerQuery,
	useUpdateCompanyLeadSyncTriggerMutation,
	useGetListLeadSyncTriggerQuery,
	useUpdateListLeadSyncTriggerMutation,
	useGetHubspotPipelinesQuery,
	useGetHubspotLeadPipelinesQuery,
	useGetHubspotOwnersQuery,
	useGetHubspotDealFieldsQuery,
	useGetDealConfigQuery,
	useSaveDealConfigMutation,
	useGetLeadConfigQuery,
	useSaveLeadConfigMutation,
	useGetUserMappingsQuery,
	useCreateUserMappingMutation,
	useDeleteUserMappingMutation,
	useCreateHubspotDealMutation,
	useLazyGetOAuthAuthorizeUrlQuery,
	useGetOAuthStatusQuery,
	useGetConversationSyncQuery,
	useUpdateConversationSyncMutation,
	useTriggerHubspotResyncMutation,
	useGetHubspotSyncLogsQuery,
	useDisconnectOAuthMutation,
} = hubspotApi;

export const hubspotApiReducer = hubspotApi.reducer;
export const hubspotApiMiddleware = hubspotApi.middleware;
